import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Camera, Send, X, Search, ArrowLeft } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const AdminChatDashboard = ({ token }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedStaff) {
      fetchMessages(selectedStaff.staff_email);
      const interval = setInterval(() => fetchMessages(selectedStaff.staff_email), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedStaff]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchMessages = async (staffEmail) => {
    try {
      const response = await fetch(
        `${API_URL}/api/chat/conversation/${staffEmail}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if ((!inputText.trim() && !selectedImage) || sending || !selectedStaff) return;

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: selectedStaff.staff_email,
          message_text: inputText.trim() || undefined,
          image_data: selectedImage || undefined
        })
      });

      if (response.ok) {
        setInputText('');
        setSelectedImage(null);
        await fetchMessages(selectedStaff.staff_email);
        await fetchConversations();
      } else {
        alert('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Maximum size is 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      compressImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const compressImage = (dataUrl) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      const maxDim = 1920;
      if (width > height && width > maxDim) {
        height = (height * maxDim) / width;
        width = maxDim;
      } else if (height > maxDim) {
        width = (width * maxDim) / height;
        height = maxDim;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const compressed = canvas.toDataURL('image/jpeg', 0.7);
      setSelectedImage(compressed);
    };
    img.src = dataUrl;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.staff_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-200px)] bg-white border rounded-lg overflow-hidden">
      {/* Conversations List Sidebar */}
      <div className={`${selectedStaff ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r bg-gray-50`}>
        <div className="p-4 border-b bg-white">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Messages</h2>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
              <MessageCircle size={48} className="mb-2" />
              <p className="text-sm text-center">No conversations yet</p>
            </div>
          )}
          {filteredConversations.map(conv => (
            <button
              key={conv.staff_email}
              onClick={() => setSelectedStaff(conv)}
              className={`w-full p-4 border-b hover:bg-white transition-colors text-left ${
                selectedStaff?.staff_email === conv.staff_email ? 'bg-white border-l-4 border-l-yellow-400' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {conv.staff_photo ? (
                    <img src={conv.staff_photo} alt={conv.staff_name} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-white font-semibold">
                      {conv.staff_name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-sm text-gray-900 truncate">
                      {conv.staff_name}
                    </h3>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-red-500 text-white text-xs">
                        {conv.unread_count}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {conv.last_sender === 'admin' ? 'You: ' : ''}
                    {conv.last_message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(conv.last_message_time).toLocaleTimeString('en-GB', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat Window */}
      <div className={`${selectedStaff ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-white`}>
        {selectedStaff ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b flex items-center gap-3">
              <button
                onClick={() => setSelectedStaff(null)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-full"
              >
                <ArrowLeft size={20} />
              </button>
              {selectedStaff.staff_photo ? (
                <img src={selectedStaff.staff_photo} alt={selectedStaff.staff_name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-white font-semibold">
                  {selectedStaff.staff_name.charAt(0)}
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-900">{selectedStaff.staff_name}</h3>
                <p className="text-xs text-gray-500">{selectedStaff.staff_email}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map(msg => (
                <MessageBubble 
                  key={msg.id} 
                  message={msg} 
                  isOwn={msg.sender_role === 'admin'} 
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Image Preview */}
            {selectedImage && (
              <div className="px-4 py-2 border-t">
                <div className="relative inline-block">
                  <img src={selectedImage} alt="Preview" className="h-20 w-20 object-cover rounded-lg" />
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Input Bar */}
            <div className="border-t px-4 py-3 flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
                disabled={sending}
              >
                <Camera size={24} className="text-gray-600" />
              </button>
              <Input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type message..."
                disabled={sending}
                className="flex-1"
              />
              <button
                onClick={sendMessage}
                disabled={(!inputText.trim() && !selectedImage) || sending}
                className="p-2 bg-yellow-400 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-500 flex-shrink-0"
              >
                {sending ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={24} />
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MessageCircle size={64} className="mb-4" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose a staff member to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
};

const MessageBubble = ({ message, isOwn }) => {
  const [imageExpanded, setImageExpanded] = useState(false);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
          isOwn 
            ? 'bg-yellow-400 text-white' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {!isOwn && (
            <p className="text-xs font-semibold text-gray-600 mb-1">
              {message.sender_name}
            </p>
          )}
          
          {message.image_data && (
            <img
              src={message.image_data}
              alt="Attachment"
              className="rounded-lg mb-2 cursor-pointer max-w-full"
              onClick={() => setImageExpanded(true)}
            />
          )}
          
          {message.message_text && (
            <p className="text-sm whitespace-pre-wrap break-words">
              {message.message_text}
            </p>
          )}
          
          <div className={`text-xs mt-1 flex items-center gap-1 ${
            isOwn ? 'text-yellow-100 justify-end' : 'text-gray-500'
          }`}>
            <span>{formatTime(message.timestamp)}</span>
            {isOwn && (
              <span>{message.read ? '✓✓' : '✓'}</span>
            )}
          </div>
        </div>
      </div>

      {imageExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setImageExpanded(false)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700"
            onClick={() => setImageExpanded(false)}
          >
            <X size={24} />
          </button>
          <img 
            src={message.image_data} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </>
  );
};

export default AdminChatDashboard;
